import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, tokenStore } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Mountain, Loader2, ShieldCheck, ArrowLeft, MailCheck } from 'lucide-react';

// Passaggio esplicito di accettazione Termini per un NUOVO utente social (Google/Apple).
// L'account NON è ancora creato: viene creato solo dopo l'accettazione.
export default function OauthCompleteRegistration() {
  const [params] = useSearchParams();
  const challenge = params.get('challenge') || '';
  const [info, setInfo] = useState(null);
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!challenge) { setError('Richiesta non valida.'); return; }
    api.get(`/auth/oauth/registration/${challenge}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Registrazione non trovata o scaduta.'));
  }, [challenge]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!accept) { setError('Devi accettare i Termini e Condizioni per continuare.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/oauth/complete-registration', { challenge, accept_terms: true });
      if (data.status === 'verify_email') {
        window.location.assign(`/verifica-email?email=${encodeURIComponent(data.email || '')}`);
      } else if (data.status === 'active' && data.access_token) {
        tokenStore.setAudience('customer');
        tokenStore.set(data.access_token, data.refresh_token);
        window.location.assign('/cliente');
      } else {
        setPending(true); // stakeholder: in attesa di approvazione
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile completare la registrazione.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div><p className="font-display text-lg font-bold">Sila Experiences</p><p className="text-xs text-muted-foreground">Completa la registrazione</p></div>
        </div>

        {pending ? (
          <div data-testid="oauth-complete-pending">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><MailCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Richiesta ricevuta</h1>
            <p className="mt-2 text-sm text-muted-foreground">La tua richiesta di adesione è in attesa di approvazione dell'amministratore. Ti avviseremo via email appena verrà approvata.</p>
            <p className="mt-6 text-sm"><Link to="/login" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Vai all'accesso</Link></p>
          </div>
        ) : !info ? (
          <div data-testid="oauth-complete-loading">
            {error ? (
              <>
                <h1 className="font-display text-2xl font-bold tracking-tight">Ops</h1>
                <p className="mt-2 text-sm text-destructive" data-testid="oauth-complete-error">{error}</p>
                <p className="mt-6 text-sm"><Link to="/login" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Torna all'accesso</Link></p>
              </>
            ) : (
              <p className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Caricamento…</p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><ShieldCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Ancora un passaggio</h1>
            <p className="mt-1 text-sm text-muted-foreground">Stai creando un account {info.role === 'customer' ? 'cliente' : 'partner'} con <span className="font-medium text-foreground">{info.email}</span>. Per continuare, accetta i Termini e Condizioni.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="oauth-complete-form">
              <label className="flex items-start gap-2.5 rounded-md border border-border bg-muted/20 p-3 text-sm" data-testid="oauth-complete-terms-row">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="oauth-complete-accept-terms-checkbox" />
                <span className="text-muted-foreground">Accetto i <Link to={info.terms_path || '/termini'} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline" data-testid="oauth-complete-terms-link">Termini e Condizioni</Link>.</span>
              </label>
              {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="oauth-complete-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="oauth-complete-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Completa la registrazione</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
